import {
  alpha,
  Box,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { FileCard } from "@pages:app/document-translation/components/FileCard";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingStateProps } from "../types";

const LOADING_MESSAGE_KEYS = [
  "translation.loading.analyzing",
  "translation.loading.detecting",
  "translation.loading.translating",
  "translation.loading.optimizing",
  "translation.loading.finalizing",
];

export const LoadingState = ({
  filename,
  size,
  language,
}: LoadingStateProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % LOADING_MESSAGE_KEYS.length);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "60px 32px",
        maxWidth: "600px",
        mx: "auto",
      }}
    >
      {/* Spinner with pulsing background */}
      <Box
        sx={{
          position: "relative",
          width: "80px",
          height: "80px",
          mb: "40px",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "100px",
            height: "100px",
            borderRadius: "50%",
            background: alpha(theme.palette.primary.main, 0.1),
            animation: "pulse 2s ease-out infinite",
            "@keyframes pulse": {
              "0%": {
                transform: "translate(-50%, -50%) scale(0.8)",
                opacity: 1,
              },
              "100%": {
                transform: "translate(-50%, -50%) scale(1.5)",
                opacity: 0,
              },
            },
          }}
        />
        <CircularProgress
          size={80}
          thickness={6}
          sx={{
            color: theme.palette.primary.main,
            "& .MuiCircularProgress-circle": {
              strokeLinecap: "round",
            },
          }}
        />
      </Box>

      {/* Status text with animated dots */}
      <Typography
        sx={{
          color: "#666468",
          fontSize: "15px",
          mb: 1,
          textAlign: "center",
        }}
        key={messageIndex}
      >
        {t(LOADING_MESSAGE_KEYS[messageIndex])}
        <Box
          component="span"
          sx={{
            display: "inline-block",
            ml: "4px",
          }}
        >
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              component="span"
              sx={{
                animation: "blink 1.4s infinite both",
                animationDelay: `${i * 0.2}s`,
                "@keyframes blink": {
                  "0%, 80%, 100%": { opacity: 0 },
                  "40%": { opacity: 1 },
                },
              }}
            >
              .
            </Box>
          ))}
        </Box>
      </Typography>

      <FileCard
        filename={filename}
        size={size}
        sx={{
          mt: "48px",
        }}
        language={language}
      />
    </Box>
  );
};
